import { Backdrop, Box, Button, Card, CardContent, CircularProgress, Container, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import { HeaderPillow } from 'pages/CustomerBooking/HeaderPillow';
import { NotFound } from 'pages/NotFound';
import { Helmet } from "react-helmet";
import React, { useCallback, useEffect, useMemo } from 'react';
import { PinInput } from 'react-input-pin-code';
import { Redirect, useHistory } from 'react-router-dom';
import { requestPinCode, requestWhitelabelVerification, useUserState } from 'services/authentication.service';
import { HelvNeue } from 'themes/theme';
import { useRunOnce, useState } from 'util/stateref';
import { AppPopup } from "../../AppPopup";
import { PrivacyPolicyPage } from './PrivacyPolicyPage';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { loginBookingState } from '..';
import { AltPinCodeBirthDay } from './AltPinCodeBirthday';
import { useSnackbar } from 'notistack';
import { useAnalytics } from '@zennya/web-component-library/src';
import { Chat } from 'pages/CustomerBooking/Chat';
import { BookingNotFound } from 'pages/CustomerBooking/Details/BookingNotFound';
import { analyticsIdentifierState } from 'pages/CustomerBooking';
import { fingerprintState } from 'util/deviceUUID';

const useStyles = makeStyles((theme) => {
  return {
    centerCard: {
      textAlign: "center",
      "& > *": {
        marginTop: theme.spacing(3)
      },
    },
    code: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(2)
    },
    codeInput: {
      backgroundColor: theme.palette.background.paper,
    },
    action: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.spacing(2)
    },
    helv: {
      fontFamily: HelvNeue
    },
    padded: {
      padding: theme.spacing(2)
    },
    popupPadding: {
      height: theme.spacing(5),
    }
  }
})
const PinCode = ({ match, location }) => {
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory();
  const classes = useStyles()
  const setBookingState = useSetRecoilState(loginBookingState)
  const serviceId = match?.params?.serviceId

  const [booking, setBooking] = useState(null);

  const [loading, setLoading] = useState();
  const [isAuthRedirect, setIsAuthRedirect] = useState(false);
  const [pinCode, setPinCode] = useState(new Array(6).fill(''));

  const [isSent, setIsSent] = useState(false);

  const [showPrivPolicy, setShowPrivPolicy] = useState(true);

  const [identifier, setIdentifier] = useState();
  const [confirming, setConfirming, confirmingRef] = useState(false);
  const [invalidCode, setInvalidCode] = useState();

  const [isValid, setIsValid] = useState(false);
  const [popupClosed, setPopupClosed] = useState(false);

  const [clientId, setClientId] = useState();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });

  const [fingerprint, setFingerprint]= useRecoilState(fingerprintState);
  const { identifyUsingIdAndTraits, trackEvent } = useAnalytics()

  const { isAuthenticated, fetchBookingByAuth, loginPinCode, loginPinCodeWithoutClient } = useUserState()
  const [analyticsIdentifier, setAnalyticsIdentifier] = useRecoilState(analyticsIdentifierState);

  const fetchAuthOrBooking = useCallback(
    async () => {
      setLoading(true);
      try {
        const result = await fetchBookingByAuth(serviceId, analyticsIdentifier)
        setAnalyticsIdentifier(result?.client?.id)

        if (!(await isAuthenticated())) {
          // await identifyUsingIdAndTraits(null, {})
          setBooking(result)
          setBookingState(result)
          setLoading(false);
        }
        else {
          history.replace(`/bookings/${serviceId}` + location.search)
        }

      } catch (err) {
        console.log('index.jsx (86) # err', err);
        history.push(`/bookings/login/${serviceId}/error` + history.location.search, {
          error: err?.response?.data
        })

      }
    },
    [serviceId, isAuthenticated],
  );

  useEffect(() => {
    fetchAuthOrBooking()
  }, [serviceId]);

  useRunOnce(() => {
    console.log(`run once: login for: ${serviceId}`)
  })

  const resendSms = useCallback(
    async () => {
      setLoading(true);
      const identResult = await requestPinCode(booking?.client?.id)
      setIdentifier(identResult)
      setClientId(booking?.client?.id)
      setIsSent(true)
      setLoading(false);
    }, [booking?.client?.id],
  );

  const sendVerification = useCallback(
    async () => {
      setLoading(true);
      const identResult = await requestWhitelabelVerification(booking?.white_label_customer?.id, serviceId)
      setIdentifier({ identifier: booking?.white_label_customer?.id })
      setClientId(booking?.white_label_customer?.id)
      setIsSent(true)
      setLoading(false);
      return identResult
    }, [booking?.white_label_customer?.id, serviceId],
  );

  const confirmCode = useCallback(
    async (pin, identifier) => {
      if (confirmingRef.current) return
      setConfirming(true)
      setInvalidCode()
      const params = {
        clientId: booking?.client?.id,
        identifier: identifier,
        code: pin,
        fingerprint
      }
      let result
      try {
        if (booking?.requires_validation) {
          await loginPinCodeWithoutClient(params)
        } else {
          await loginPinCode(params)
        }
        setIsValid(true)
        trackEvent('Verification Page Birthdate - confirmed', { booking_id: serviceId })
      } catch (e) {
        if (e?.response) {
          setInvalidCode((e?.message?.en || e?.response?.data?.message?.en || e?.response?.data?.details) ?? "Invalid code")
          
          const errMessage = (e?.message?.en || e?.response?.data?.message?.en || e?.response?.data?.details) ?? "Invalid code"
          enqueueSnackbar(JSON.stringify(errMessage), { variant: 'error' })

          setIsValid(false)
          trackEvent('Verification Page Error - code autoentry', { booking_id: serviceId, error: errMessage, code: pin, identifier, client_id: booking?.client?.id })
        } else {
          enqueueSnackbar(JSON.stringify(e?.message), { variant: 'error' })
        }
      }
      setPinCode(new Array(6).fill(''))
      setConfirming(false)
    }, [booking, clientId, confirmingRef, fingerprint, identifier?.identifier, loginPinCode, loginPinCodeWithoutClient, setConfirming],
  );

  const doExistenceCheck = useCallback(
    () => {
      if (booking?.requires_validation) {
        history.push(`/bookings/login/${serviceId}/existing` + history.location.search, booking)
      } else {
        setShowPrivPolicy(false)
      }
    },
    [booking, history, serviceId],
  );

  const renderSendPinDestination = useMemo(() => {
    if (booking?.requires_validation) {
      return (
        <>
          <Typography className={classes.helv} variant="h3">Verification</Typography>
          <Box>
            <CardContent>
              <Typography variant="body2">Open your Zennya App and input the pin displayed on the next screen</Typography>
              <Button variant="contained" color="primary" onClick={() => doExistenceCheck()}>Continue</Button>
            </CardContent>
          </Box>
        </>
      )
    } else {
      if (booking?.validate_customer) {
        return (
          <AltPinCodeBirthDay
            customer={booking?.customer}
            client={booking?.client}
            serviceId={serviceId}
            onVerificationComplete={({ identifier, code, expiry_date }) => {
              confirmCode(code, identifier)
            }}
          />
        )

      }
    }
  }, [booking?.client, booking?.customer?.id, booking?.requires_validation, booking?.validate_customer, classes.helv, doExistenceCheck]);

  const onPrivPolAgree = useCallback(
    async () => {
      setShowPrivPolicy(false)
      if (booking?.requires_validation) {
        // await sendVerification()
        history.push(`/bookings/login/${serviceId}/existing` + history.location.search, booking)
      }
    },
    [booking, history, sendVerification, serviceId],
  );

  const renderTitle = useMemo(
    () => {
      const truncateID = serviceId?.substring(serviceId?.length - 4)
      return (
        <Helmet>
          <title>
            {`Zennya Health - ${booking?.white_label_partner?.label ?? ''} Booking (${truncateID})`}
          </title>
          <meta
            name="description"
            content={`Zennya Health - ${booking?.white_label_partner?.label ?? ''} Booking (${truncateID})`}
          />
        </Helmet>
      )
    }, [booking?.white_label_partner?.label, serviceId]
  );
  
  if (!serviceId) {
    return <BookingNotFound />
  }

  if (isValid) {
    return <Redirect to={{
      ...history.location,
      pathname: `/bookings/${serviceId}`
    }} />
  }

  return (
    <>
      {/* <Chat serviceId={serviceId} /> */}
      {renderTitle}
      <AppPopup onClose={() => setPopupClosed(true)} />
      <Container maxWidth="sm" className={classes.centerCard} disableGutters>
        <HeaderPillow whiteLabelPartner={booking?.white_label_partner} />
        {loading ? <CircularProgress /> : (
          <>
            {showPrivPolicy ? <PrivacyPolicyPage serviceId={serviceId} bookingData={booking} whiteLabelPartner={booking?.white_label_partner} onComplete={() => { onPrivPolAgree() }} />
              :
              !isSent ? (
                <>{renderSendPinDestination}</>
              ) : (
                <>
                  <Typography className={classes.helv} variant="h3">Verification</Typography>
                  {confirming ? <CircularProgress /> : (
                    <Box>
                      <CardContent>
                        <Typography variant="body1">
                          {booking?.requires_validation ?
                            `Enter One Time PIN on your mobile app`
                            :
                            `Enter One Time PIN sent to your mobile number`
                          }
                        </Typography>
                        <div className={classes.code}>
                          <PinInput
                            className={classes.codeInput}
                            values={pinCode}
                            size="lg"
                            placeholder=""
                            onChange={(char, index, pinString) => {
                              setPinCode(pinString)
                            }}
                            onComplete={(v) => {
                              confirmCode(v.join(''))
                            }} />
                          <div className={classes.action}>
                            {invalidCode && <Typography variant="caption" color="error">{invalidCode}</Typography>}
                            {/* <Typography variant="caption" color="textSecondary">{moment(identifier?.expiry_date).tz("Asia/Manila").format('LL LT')}</Typography> */}

                            <Button
                              disabled={confirming}
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                if (booking?.requires_validation) {
                                  sendVerification()
                                } else {
                                  resendSms()
                                }
                              }}
                            >Resend OTP</Button>
                          </div>
                        </div>
                      </CardContent>
                    </Box>
                  )}
                </>
              )}
          </>
        )}
      </Container>

    </>
  )
}

export { PinCode };
